import axios from "axios";
import {
  GESTOR_CENTRALIZADO_BASE_URL,
  GESTOR_CENTRALIZADO_GENERAR_PATH,
  GESTOR_CENTRALIZADO_GUARDAR_PATH,
  GESTOR_CENTRALIZADO_OBSERVACION_PATH,
  GESTOR_CENTRALIZADO_OBSERVAR_PATH,
  GESTOR_CENTRALIZADO_PATH,
  GESTOR_CENTRALIZADO_PENALIDADES_CATALOGO_PATH,
  GESTOR_CENTRALIZADO_REQUEST_TIMEOUT,
  GESTOR_CENTRALIZADO_USE_MOCK,
  MESSAGES,
  SERVICE_RECORDS_BASE_URL,
  GESTOR_CENTRALIZADO_AGREGAR_MANUAL_PATH
} from "./gestor-centralizado.constants";
import {
  buildGestorCentralizadoQueryParams,
  buildGuardarGestorCentralizadoPayload,
  buildObservacionGestorCentralizadoPayload,
  getDirtyGestorCentralizadoRows,
  parseGestorCentralizadoErrorMessage,
  resolveEffectiveIdPeriodo,
  resolveGestorCentralizadoPagedTotal,
} from "./gestor-centralizado.contract";
import {
  mapGestorCentralizadoEditableResponse,
  mapGestorCentralizadoResponse,
} from "./gestor-centralizado.mapper";
import gestorCentralizadoMock from "./mock/gestor-centralizado.mock.json";

const JSON_HEADERS = { "Content-type": "application/json" };

const REQUEST_CONFIG = {
  headers: JSON_HEADERS,
  timeout: GESTOR_CENTRALIZADO_REQUEST_TIMEOUT,
};

function buildApiUrl(base, path, envName) {
  if (!base) {
    throw new Error(`Falta la variable de entorno ${envName}.`);
  }

  return `${base}${path}`;
}

function buildQueryString(params = {}) {
  const entries = Object.entries(params).filter(([, value]) => {
    if (value === null || value === undefined || value === "") return false;
    if (value === 0 || value === "0") return false;
    return true;
  });

  if (!entries.length) return "";

  const query = entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join("&");

  return `?${query}`;
}

function normalizePagination(pagination = {}) {
  return {
    page: pagination.page ?? 1,
    pageSize:
      pagination.pageSize ?? pagination.perPage ?? pagination.limit ?? 10,
  };
}

function mapFetchResponse(data, paging, editable = true) {
  const mapper = editable
    ? mapGestorCentralizadoEditableResponse
    : mapGestorCentralizadoResponse;
  const mapped = mapper(data, paging);

  return {
    ...mapped,
    total: resolveGestorCentralizadoPagedTotal(data, mapped.items, paging),
  };
}

export function getGestorCentralizadoErrorMessage(error) {
  return parseGestorCentralizadoErrorMessage(error, MESSAGES.LOAD_ERROR);
}

function postGestorCentralizado(path, payload, envName = "VUE_APP_REPORT_VTEX_BASE") {
  if (GESTOR_CENTRALIZADO_USE_MOCK) {
    return Promise.resolve({ data: { success: true } });
  }

  const url = buildApiUrl(GESTOR_CENTRALIZADO_BASE_URL, path, envName);
  return axios.post(url, payload, REQUEST_CONFIG);
}

function buildCabeceraPayload(idLiquidacionCab, usuario) {
  return {
    idLiquidacionCab: Number(idLiquidacionCab),
    usuario,
  };
}

export async function fetchGestorCentralizado(
  filters = {},
  pagination = {},
  options = {},
) {
  const paging = normalizePagination(pagination);
  const params = buildGestorCentralizadoQueryParams(filters, paging);
  const editable = options.editable !== false;

  if (!resolveEffectiveIdPeriodo(filters)) {
    throw new Error(MESSAGES.SELECT_PERIODO_HINT);
  }

  if (GESTOR_CENTRALIZADO_USE_MOCK) {
    return mapFetchResponse(gestorCentralizadoMock, paging, editable);
  }

  const url =
    buildApiUrl(
      GESTOR_CENTRALIZADO_BASE_URL,
      GESTOR_CENTRALIZADO_PATH,
      "VUE_APP_REPORT_VTEX_BASE",
    ) + buildQueryString(params);

  const { data, status } = await axios.get(url, REQUEST_CONFIG);

  if (data?.success === false) {
    const error = new Error(data.message || MESSAGES.LOAD_ERROR);
    error.response = { data, status: status || 400 };
    throw error;
  }

  return mapFetchResponse(data, paging, editable);
}

export async function fetchPenalidadesCatalogo() {
  const url = buildApiUrl(
    GESTOR_CENTRALIZADO_BASE_URL,
    GESTOR_CENTRALIZADO_PENALIDADES_CATALOGO_PATH,
    "VUE_APP_REPORT_VTEX_BASE",
  );

  const { data } = await axios.get(url, REQUEST_CONFIG);
  return Array.isArray(data) ? data : [];
}
export async function buscarOsPorSecuencia(sequence, options = {}) {
  if (!SERVICE_RECORDS_BASE_URL) {
    throw new Error("Falta la variable de entorno VUE_APP_SERVICE_RECORDS_BASE.");
  }

  const today = new Date().toISOString().slice(0, 10);

  const { data } = await axios.get(`${SERVICE_RECORDS_BASE_URL}/records`, {
    timeout: GESTOR_CENTRALIZADO_REQUEST_TIMEOUT,
    params: {
      state: "TODOS",
      sellerNote: "",
      sequence,
      startCreationDate: options.startDate || "2020-01-01",
      endCreationDate: options.endDate || today,
      page: 1,
      limit: 10,
    },
    headers: {
      ...JSON_HEADERS,
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
    },
  });

  return Array.isArray(data) ? data : [];
}
export async function saveGestorCentralizado(items, idLiquidacionCab, usuario) {
  const payload = buildGuardarGestorCentralizadoPayload(
    items,
    idLiquidacionCab,
    usuario,
  );

  if (!getDirtyGestorCentralizadoRows(items).length) {
    const error = new Error(MESSAGES.NO_CHANGES_TO_SAVE);
    error.code = "NO_CHANGES";
    throw error;
  }

  await postGestorCentralizado(GESTOR_CENTRALIZADO_GUARDAR_PATH, payload);
}

export async function confirmarObservacionGestor(row, usuario) {
  const payload = buildObservacionGestorCentralizadoPayload(row, usuario);

  if (!payload.detSequenceId) {
    const error = new Error(MESSAGES.CONFIRM_OBS_ERROR);
    error.code = "INVALID_ROW";
    throw error;
  }

  await postGestorCentralizado(GESTOR_CENTRALIZADO_OBSERVACION_PATH, payload);
}

export async function observarPreLiquidacionGestor(idLiquidacionCab, usuario) {
  await postGestorCentralizado(
    GESTOR_CENTRALIZADO_OBSERVAR_PATH,
    buildCabeceraPayload(idLiquidacionCab, usuario),
  );
}

export async function generarLiquidacionGestor(idLiquidacionCab, usuario) {
  await postGestorCentralizado(
    GESTOR_CENTRALIZADO_GENERAR_PATH,
    buildCabeceraPayload(idLiquidacionCab, usuario),
  );
}
export async function agregarOsManualGestor(idLiquidacionCab, idOsDet, usuario, adicionales = [], cantidad = null) {
  await postGestorCentralizado(GESTOR_CENTRALIZADO_AGREGAR_MANUAL_PATH, {
    idLiquidacionCab: Number(idLiquidacionCab),
    idOsDet: Number(idOsDet),
    usuario,
    adicionales,
    cantidad,
  });
}